import useAxiosRequest from "../hooks/useAxiosRequest";

import '../styles/MapTemplateEditor.css';
import {ColumnGrid, GridRow} from "../components/ColumnGrid";
import OverlayDiv from "../components/OverlayDiv";
import {createContext, useContext, useEffect, useState} from "react";
import TemplateDivisionTypeElement from "../components/TemplateDivisionTypeElement";
import useUpdateImageInterval from "../hooks/useUpdateImageInterval";
import DivisionTypeCreator from "../components/DivisionTypeCreator";
import LegendItemCreator from "../components/LegendItemCreator";
import TemplateLegendItemElement from "../components/TemplateLegendItemElement";
import Button from "../components/Button";
import FormData from "form-data";
import axios from "axios";
import Modal from "../components/Modal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileImage} from "@fortawesome/free-solid-svg-icons";
import {Buffer} from "buffer";
import config from '../config.json';

const SidebarItem = ({ children }) => {
    return <div className="sidebar-item">{children}</div>
}

export const TemplateContext = createContext(null);
export const SelectedDivisionContext = createContext(null);

export const findDivision = (id, object, depth = 0) => {
    for (const divisionType of object.division_types ?? []) {
        const result = findDivision(id, divisionType, depth + 1);
        if (result) return result;
    }

    for (const division of object.divisions ?? []) {
        if (division?.id === id) return { depth, result: division };

        const result = findDivision(id, division, depth + 1);
        if (result) return result;
    }
};

export const findAllSubdivisions = (object) => {
    const divisions = object.divisions ?? [];

    divisions.push(...(object.divisions?.map(division => findAllSubdivisions(division)).flat() ?? []));

    return divisions;
}

export const findDivisionType = (id, object, depth = 0) => {
    for (const divisionType of object.division_types ?? []) {
        if (divisionType?.id === id) return { depth, result: divisionType };

        const result = findDivisionType(id, divisionType, depth + 1);
        if (result) return result;
    }

    for (const division of object.divisions ?? []) {
        const result = findDivisionType(id, division, depth + 1);
        if (result) return result;
    }
};

const UploadModal = ({ showModal, setShowModal }) => {
    const { setTemplateImage } = useContext(TemplateContext);

    const [ file, setFile ] = useState();

    const upload = async () => {
        setShowModal(false);

        const reader = new FileReader();
        reader.readAsText(file, "UTF-8");
        reader.onload = e => setTemplateImage(Buffer.from(e.target.result).toString('base64'));
    };

    const footer = (
        <div style={{ display: "flex", gap: "20px" }}>
            <Button color="#40b319" onClick={upload} content="Upload"/>
            <Button color="#c20a0a" onClick={() => setShowModal(false)} content="Cancel"/>
        </div>
    );

    return (
        <Modal
            header="Upload Image"
            footer={footer}
            show={showModal}
            onClose={() => setShowModal(false)}>
            <ColumnGrid columns="20px 1fr 1fr">
                <GridRow>
                    <FontAwesomeIcon icon={faFileImage}/>
                    <span>File</span>
                    <input type="file" accept="image/svg+xml" onChange={e => setFile(e.target.files[0])} />
                </GridRow>
            </ColumnGrid>
        </Modal>
    );
}

const MapTemplateEditor = ({ id }) => {
    let {isLoading, data: template, setData: setTemplate, error} = useAxiosRequest({
        url: `${config.backendUrl}/maps/templates/${id}`,
        withCredentials: true,
        method: "get"
    });

    const updateTemplate = (modifiedTemplate) => {
        delete modifiedTemplate.image;

        axios({
            url: `${config.backendUrl}/maps/templates/${template._id}`,
            withCredentials: true,
            method: "put",
            data: modifiedTemplate
        })
            .then(response => setTemplate(response.data))
            .catch(error => alert(error.toString()));
    }

    const updateTemplateImage = (modifiedImage) => {
        const formData = new FormData();

        formData.append("image", modifiedImage);
        formData.append("id", template._id);

        axios({
            url: `${config.backendUrl}/maps/templates/image/${template._id}`,
            withCredentials: true,
            method: "post",
            headers: { "Content-Type": "multipart/form-data" },
            data: formData
        })
            .then(response => setTemplate(response.data))
            .catch(error => console.log(error));
    }

    const [ selectedDivision, setSelectedDivision ] = useState(null);

    const [ imageEdited, setImageEdited ] = useState(false);

    useUpdateImageInterval(template, imageEdited, setImageEdited);

    const [ showUploadModal, setShowUploadModal ] = useState(false);

    const updateSelectedDivision = id => {
        document.querySelectorAll('path.highlight').forEach(element => element.classList.remove('highlight'));

        console.log('Selected ' + id);
        setSelectedDivision(id);

        if (id === null) return;

        const division = findDivision(id, template);
        const subdivisions = findAllSubdivisions(division.result);

        document.querySelectorAll(`path.division-${id}`).forEach(element => element.classList.add('highlight'));
        subdivisions.forEach(division => document.querySelectorAll(`path.division-${division.id}`).forEach(element => element.classList.add('highlight')));
    }

    if (isLoading) return <p>Loading...</p>;

    if (error) return alert("There is an error.");

    const image = atob(template.image);

    const test = () => {
        console.log('aha');
        return image;
    }

    return (
        <TemplateContext.Provider value={{ template, setTemplate: updateTemplate, setTemplateImage: updateTemplateImage }}>
            <SelectedDivisionContext.Provider value={{ selectedDivision, setSelectedDivision: updateSelectedDivision }}>
                <div id="MapTemplateEditor">
                    <UploadModal showModal={showUploadModal} setShowModal={setShowUploadModal} divisionId={id}/>

                    <div id="sidebarL" className="sidebar" onClick={() => updateSelectedDivision(null)}>
                        <SidebarItem>
                            <ColumnGrid columns="1fr auto">
                                <GridRow>
                                    <span style={{ fontWeight: 700, margin: "auto 0" }}>Division Types</span>
                                    <DivisionTypeCreator/>
                                </GridRow>
                            </ColumnGrid>
                        </SidebarItem>
                        {template.division_types.map(divisionType => <TemplateDivisionTypeElement key={divisionType.id} id={divisionType.id}/>)}
                    </div>
                    <div id="main-content">
                        <OverlayDiv>
                            <div id="image-container" dangerouslySetInnerHTML={{__html: test()}}
                                 onClick={e => {
                                     if (!selectedDivision) return;

                                     const elements = document.elementsFromPoint(e.nativeEvent.clientX, e.nativeEvent.clientY);

                                     if (elements[0]?.tagName !== 'path') return;

                                     const element = elements[0];

                                     element.classList.toggle('division-' + selectedDivision);
                                     element.classList.toggle('highlight');

                                     console.log('Toggled path from ' + selectedDivision);
                                     setImageEdited(true);
                                 }}>
                            </div>
                        </OverlayDiv>
                    </div>
                    <div id="sidebarR" className="sidebar">
                        <SidebarItem>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Button content='Upload Image' onClick={() => setShowUploadModal(true)}/>
                            </div>
                        </SidebarItem>
                        <SidebarItem>
                            <ColumnGrid columns="1fr auto">
                                <GridRow>
                                    <span style={{ fontWeight: 700, margin: "auto 0" }}>Legend Items</span>
                                    <LegendItemCreator/>
                                </GridRow>
                            </ColumnGrid>
                        </SidebarItem>
                        {template.legend.map(item => <TemplateLegendItemElement key={item.id} item={item}/>)}
                    </div>
                </div>
            </SelectedDivisionContext.Provider>
        </TemplateContext.Provider>
    );
}

export default MapTemplateEditor;
